import "./assets/scss/questionnaire.scss";

import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import ChoiceButtons from "@components/elements/ChoiceButtons";
import DatePicker from "@components/elements/DatePicker";
import DateRangePicker, { DateRangePickerValue } from "@components/elements/DateRangePicker";
import { PageConsultation } from "@components/layouts/PageConsultation";
import BiometricsPage, {
  Biometrics,
} from "@components/pages/Consultation/Components/BiometricsPage";
import ParentalConsent, {
  ParentalConsentData,
} from "@components/pages/Consultation/Components/ParentalConsent";
import StreamlinedConsultationPage from "@components/pages/Consultation/Components/StreamlinedConsultationPage";
import UpsellPage from "@components/pages/Consultation/Components/UpsellPage";
import AutocompleteInput from "@elements/AutocompleteInput";
import Checkbox from "@elements/Checkbox";
import MedicationAutocomplete, {
  MedicationAutocompleteHandle,
} from "@elements/MedicationAutocomplete";
import RadioButton from "@elements/RadioButton";
import useStreamlinedSelection from "@hooks/recommendations/useStreamlinedSelection";
import useQuestionnaireIndex from "@hooks/questionnaire/useQuestionnaireIndex";
import { useQuestionValidation } from "@hooks/questionnaire/useQuestionValidation";
import Loader from "@img/Loader.svg";
import {
  AlertBanner,
  AlertBannerMessage,
  Button,
  Feedback,
  FormGroup,
  Text,
  TextField,
  Title,
} from "@runwayhealth/runway-components-react";
import { clearSuccess } from "@store/user/userSlice";
import { CaseUpdate, RecommendedConditions } from "@types-runway/case";
import { Condition } from "@types-runway/condition";
import {
  Answer,
  AnswerType,
  Calendar,
  CalendarAnswerDTO,
  CUSTOM_OPTION_TITLE,
  NoticeMessage,
  Page,
  Question,
} from "@types-runway/questionnaire";
import { Partner, RootState } from "@types-runway/state";
import { Gender, User, UserUpdate } from "@types-runway/user";
import { composeOverrideQuestions, composeQuestion } from "@utils/questionnaire/answerComposition";
import { determineSkipDirection } from "@utils/questionnaire/skipDirection";

export type OnNextPayload = {
  addonConditions?: Condition[];
  question?: Question;
  skipUpdate?: boolean;
};

interface QuestionnaireProps {
  currentPage: Page;
  onNext: (payload?: OnNextPayload) => void;
  onPrev: () => void;
  pageIndex: number;
  initialValue?: Question;
  onAnswer: (question: Question, stateKey?: string, skipQuestionnaireUpdate?: boolean) => void;
  bannerContent: NoticeMessage | null;
  setBannerContent: React.Dispatch<React.SetStateAction<NoticeMessage | null>>;
  partner: Partner | null;
  visitFeeDiscount?: number;
  addonRecommendations: RecommendedConditions;
  regionRecommendations: RecommendedConditions;
  checkRenderAllowed: (page: Page) => boolean;
  pageIsLoading: boolean;
  isRegionLoading: boolean;
  isAuth: boolean;
}

export default function Questionnaire({
  currentPage,
  onNext,
  onPrev,
  pageIndex,
  initialValue,
  onAnswer,
  bannerContent,
  setBannerContent,
  partner,
  visitFeeDiscount,
  addonRecommendations,
  regionRecommendations,
  checkRenderAllowed,
  pageIsLoading,
  isRegionLoading,
  isAuth,
}: QuestionnaireProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [fieldValue, setFieldValue] = useState<AnswerType | null>(null);
  const user = useSelector<RootState, User>((state) => state.user.data);
  const [openAutocompleteDropdown, setOpenAutocompleteDropdown] = useState(false);
  // This decouples the chosen option from field value
  const [inputChosenOption, setInputChosenOption] = useState<string | null>(null);
  // Show text input
  const [buttonThenTextField, setButtonThenTextField] = useState(false);
  // Checkbox textfield.
  const [checkboxTextFieldIsOpen, setCheckboxTextFieldIsOpen] = useState(false);
  const [checkboxTextFieldValue, setCheckboxTextFieldValue] = useState("");
  // Raw, not-yet-committed MedicationAutocomplete text -- kept separate from
  // fieldValue so the Next button isn't disabled mid-typing. See the
  // docs/domains/medication-search-input/index.md for why this can't just be fieldValue.
  const [medicationPendingInput, setMedicationPendingInput] = useState("");
  // Lets the Next button read MedicationAutocomplete's true current value
  // without depending on blur having committed it first. See the
  // docs/domains/medication-search-input/index.md for why (Next button's onMouseDown).
  const medicationAutocompleteRef = useRef<MedicationAutocompleteHandle>(null);
  // Index of the page the patient navigated away from. Starts out null so
  // the first effect run can tell a resume onto a hidden page apart from a
  // repeat render at the same index (see determineSkipDirection).
  const navigationIndex = useRef<number | null>(null);
  const { isValidAnswer, displayWarningMessage } = useQuestionValidation();
  const [warningMessage, setWarningMessage] = useState(false);
  const { success: userUpdateSuccess } = useSelector((state: RootState) => state.user);
  const { clearIndex } = useQuestionnaireIndex();
  const shouldRender = useMemo(() => {
    return checkRenderAllowed(currentPage);
  }, [currentPage]);
  const [comfortCareRecommendations, setComfortCareRecommendations] =
    useState<RecommendedConditions | null>(null);
  const streamlinedSelection = useStreamlinedSelection();

  const skipQuestion = (previousIndex: number | null, currentIndex: number) => {
    const skipBackward = determineSkipDirection(previousIndex, currentIndex) === "backward";

    if (skipBackward) {
      onPrev();
      const questionnaireIsAtFirstPage = currentIndex === 0;

      if (questionnaireIsAtFirstPage) {
        // Clear index to restart questionnaire.
        clearIndex();
      }
    } else {
      // If nav is moving forward, skip forward.
      onNext({ skipUpdate: true });
    }
  };

  const processAnswer = (answer: Answer): void => {
    // Always update local state
    setFieldValue(answer.content);

    // Show notice message if any needs to trigger.
    const warningMessage = displayWarningMessage(answer.content, currentPage.warningMessage);
    setBannerContent(warningMessage);
    setWarningMessage(warningMessage !== null);

    // Always update Redux store if stateKey exists
    if (currentPage.stateKey) {
      const hasOverrides = !!(
        currentPage.actorOverrides && currentPage.actorOverrides.overrides.length > 0
      );
      const composedQ = composeQuestion(currentPage, answer);

      if (hasOverrides) {
        const requiresMapping =
          currentPage.storageMappings && currentPage.storageMappings.mappings.length > 0;

        // If answer requires mapping, save parent question before the overrides.
        if (requiresMapping) {
          onAnswer(composedQ, currentPage.stateKey, true);
        }

        const overrideResults = composeOverrideQuestions(currentPage, answer);
        overrideResults.forEach(({ stateKey, question }) => {
          onAnswer(question, stateKey);
        });
      } else {
        onAnswer(composedQ);
      }
    }
  };

  // Resets Questionnaire-owned per-question UI state on question change. Must not
  // depend on initialValue, because that changes after every answer.
  useEffect(() => {
    setButtonThenTextField(false);
    setCheckboxTextFieldIsOpen(false);
    setCheckboxTextFieldValue("");
    setInputChosenOption(null);
  }, [currentPage.stateKey]);

  // Update local state when initialValue changes
  useEffect(() => {
    if (currentPage.stateKey) {
      // A stored "No" is the answer to the Yes/No gate, not follow-up text: seeding the
      // revealed field with it would pre-fill a "No" chip/text once the patient picks Yes.
      const isGateAnswerNo =
        currentPage.inputType.input === "buttons_then_text" && initialValue?.answer === "No";
      setFieldValue(isGateAnswerNo ? null : (initialValue?.answer ?? null));
      // Never let uncommitted text typed on one page bleed into the next.
      setMedicationPendingInput("");

      // Re-expand the medication-search input after a reload/resume. Scoped
      // to this variant only -- see the docs/domains/medication-search-input/index.md
      // for why generalizing this to all buttons_then_text questions isn't safe.
      if (currentPage.inputType.variant === "medication-search") {
        const answer = initialValue?.answer;
        setButtonThenTextField(!!answer && answer !== "No" && answer.toString().length > 0);
      }
    }
  }, [currentPage.stateKey, initialValue]);

  useMemo(() => {
    if (userUpdateSuccess.type === "write") {
      dispatch(clearSuccess());
    }
  }, [userUpdateSuccess, dispatch]);

  useEffect(() => {
    // Catches previous navigation index before update.
    const previousIndex = navigationIndex.current;
    // Updates navigation index to current.
    navigationIndex.current = pageIndex;

    if (currentPage.deriveAnswerFromState) {
      const answerStateSource = currentPage.deriveAnswerFromState.source;
      switch (answerStateSource) {
        case "partner":
          if (partner) {
            let answerContent = partner[
              currentPage.deriveAnswerFromState.key as keyof Partner
            ] as string;
            processAnswer({
              content: answerContent === "insurance" ? "true" : answerContent,
            });
          }
          break;
      }
    }

    if (!shouldRender) {
      skipQuestion(previousIndex, navigationIndex.current);
    }
  }, [currentPage, shouldRender]);

  const renderNextButton =
    buttonThenTextField ||
    (currentPage.inputType.input !== "buttons" &&
      currentPage.inputType.input !== "component" &&
      currentPage.inputType.input !== "buttons_then_text");

  const renderPage = (page: Page) => {
    return (
      <>
        {pageIsLoading ? (
          <div
            className="transition-content-wrapper"
            data-testid={`question-loader-${page.stateKey}`}
          >
            <img className="loader-img" src={Loader} />
            <Title as="h1" size="giant">
              Taking off 🛫
            </Title>
          </div>
        ) : (
          <PageConsultation
            key={page.stateKey ?? page.question ?? pageIndex}
            data-testid={`question-${page.stateKey}`}
            data-type={page.inputType.input}
            data-context={page.stateKey}
          >
            {page.inputType.input !== "component" && (
              <PageConsultation.Header>
                <Title size="giant">{page.question}</Title>
                <Text size="md">{page.reason}</Text>
              </PageConsultation.Header>
            )}
            <PageConsultation.Body>
              {page.inputType.input === "text" && (
                <>
                  <FormGroup>
                    <TextField
                      data-testid="questionnaire-text-input"
                      placeholder={page.placeholder}
                      value={fieldValue || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue(e.target.value)
                      }
                    />
                  </FormGroup>
                </>
              )}
              {page.inputType.input === "textarea" && (
                <>
                  <FormGroup>
                    <TextField
                      data-testid="questionnaire-textarea"
                      as="textarea"
                      placeholder={page.placeholder}
                      value={fieldValue || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        processAnswer({ content: e.target.value })
                      }
                    />
                  </FormGroup>
                </>
              )}
              {page.inputType.input === "buttons" && (
                <>
                  <ChoiceButtons
                    onClick={(option) => {
                      let important = false;
                      if (option.action === "end") {
                        if (page.noticeMessage?.message && page.noticeMessage.variant) {
                          setBannerContent({
                            message: page.noticeMessage?.message,
                            variant: page.noticeMessage?.variant,
                          });
                        }
                        return;
                      }

                      if (option.title === "No") {
                        important = page.important?.polarity === "negative";
                      }

                      if (option.title === "Yes") {
                        important = page.important?.polarity === "positive";
                      }

                      const answerMap = new Map<string, string>();
                      answerMap.set("Yes", "true");
                      answerMap.set("No", "false");

                      processAnswer({
                        content: answerMap.get(option.title) ?? option.title,
                        important,
                      });
                      onNext();
                    }}
                    options={page.options ?? []}
                  />
                </>
              )}
              {page.inputType.input === "date" && (
                <div className="mb-4">
                  <DatePicker
                    id="date-input"
                    value={fieldValue?.toString() ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue(e.target.value);
                      if (new Date(e.target.value).toString() !== "Invalid Date") {
                        processAnswer({ content: e.target.value as string });
                      }
                    }}
                    ariaLabel={page.question}
                  />
                </div>
              )}
              {page.inputType.input === "autocomplete" && (
                <FormGroup>
                  <AutocompleteInput
                    open={openAutocompleteDropdown}
                    options={page.options?.map((option) => option.title) ?? []}
                    onKeyUp={() => {
                      setBannerContent(null);
                      setOpenAutocompleteDropdown(true);
                    }}
                    onFocus={() => setOpenAutocompleteDropdown(true)}
                    capturedVariant={(variant) => {
                      processAnswer({ content: variant as string });
                      setOpenAutocompleteDropdown(false);
                    }}
                    placeholder={page.placeholder}
                    defaultValue={fieldValue as string}
                  />
                </FormGroup>
              )}
              {page.inputType.input === "buttons_then_text" && (
                <FormGroup>
                  <ChoiceButtons
                    onClick={(option) => {
                      let important = false;
                      let answer = option.title;

                      if (option.title === "Yes") {
                        important = page.important?.polarity === "positive";
                        setButtonThenTextField(true);
                        return;
                      }

                      if (option.title === "No") {
                        important = page.important?.polarity === "negative";
                        setButtonThenTextField(false);
                      }

                      processAnswer({
                        content: answer,
                        important,
                      });

                      onNext();
                    }}
                    options={page.options ?? []}
                  />
                  {buttonThenTextField && (
                    <FormGroup className="textarea">
                      {page.inputType.variant === "medication-search" ? (
                        <MedicationAutocomplete
                          ref={medicationAutocompleteRef}
                          value={(fieldValue as string) ?? ""}
                          onChange={(value: string) => {
                            const important = page.important?.polarity === "positive";
                            processAnswer({ content: value, important });
                          }}
                          onInputChange={setMedicationPendingInput}
                          placeholder={page.placeholder}
                        />
                      ) : (
                        <TextField
                          data-testid="buttons-then-text-questionnaire-input"
                          as="textarea"
                          placeholder={page.placeholder ?? "Please specify"}
                          value={fieldValue as string}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            const value = e.target.value;
                            setFieldValue(value);

                            const warning = displayWarningMessage(value, page.warningMessage);
                            setBannerContent(warning);
                          }}
                        />
                      )}
                    </FormGroup>
                  )}
                </FormGroup>
              )}
              {page.inputType.input === "checkbox" && (
                <div className="rw-page-consultation-list">
                  {page.options
                    ?.map((option) => option.title)
                    ?.slice()
                    ?.sort((a: string, b: string) => {
                      if (a === "None of the above") return 1;
                      if (b === "None of the above") return -1;
                      if (a === CUSTOM_OPTION_TITLE) return 1;
                      if (b === CUSTOM_OPTION_TITLE) return -1;
                      return a.localeCompare(b);
                    })
                    .map((option: string, index: number) => {
                      const convertedFieldValue = (inputChosenOption as string)?.split("|") ?? [];
                      const inputOptions = (convertedFieldValue as string[]) ?? [];
                      const checked =
                        inputOptions.includes(option) ||
                        (option === CUSTOM_OPTION_TITLE && checkboxTextFieldIsOpen);

                      let newValue: string[];
                      if (option === "None of the above") {
                        newValue = !checked ? ["None of the above"] : [];
                      } else {
                        const withoutNone = ((convertedFieldValue as string[]) ?? []).filter(
                          (o) => o !== "None of the above"
                        );
                        if (checked) {
                          newValue = withoutNone.filter((o) => o !== option);
                        } else {
                          newValue = [...withoutNone, option];
                        }
                      }

                      return (
                        <Checkbox
                          data-testid={`checkbox-input-${index}`}
                          key={option}
                          content={option}
                          checked={checked}
                          setChecked={() => {
                            if (option === CUSTOM_OPTION_TITLE) {
                              setCheckboxTextFieldIsOpen(!checkboxTextFieldIsOpen);
                            }

                            if (option === "None of the above") {
                              setCheckboxTextFieldIsOpen(false);
                            }
                            processAnswer({ content: newValue.join("|") });
                            setInputChosenOption(newValue.join("|"));
                          }}
                          disabled={false}
                          required={false}
                          num={index}
                          onClick={() => {}}
                        />
                      );
                    })}
                  {checkboxTextFieldIsOpen && (
                    <TextField
                      as="textarea"
                      placeholder={page.placeholder ?? "Please specify"}
                      value={checkboxTextFieldValue}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setCheckboxTextFieldValue(e.target.value);
                        const options = (inputChosenOption as string)?.split("|") ?? [];
                        const customTitleIndex = options.findIndex(
                          (option) => option === CUSTOM_OPTION_TITLE || option.includes("-")
                        );
                        if (options.includes(CUSTOM_OPTION_TITLE)) {
                          options[customTitleIndex] = "-";
                        }
                        options[customTitleIndex] = `- ${e.target.value}`;
                        setInputChosenOption(options.join("|"));
                      }}
                    />
                  )}
                </div>
              )}
              {page.inputType.input === "component" && (
                <>
                  {page.inputType.context === "addon" && (
                    <UpsellPage
                      title={page.question}
                      visitFeeDiscount={visitFeeDiscount}
                      onNext={onNext}
                      addonRecommendations={addonRecommendations}
                      warningMessage={page.warningMessage}
                    />
                  )}
                  {page.inputType.context === "welcomeBack" && (
                    <>
                      <Title size="giant">{`${page.question}, ${user.first_name}`}</Title>
                      <Text size="md">{page.reason}</Text>
                      <ChoiceButtons
                        onClick={(option) => {
                          if (option.action === "end") {
                            history.push("/dashboard?referral=true");
                          } else {
                            onNext();
                          }
                        }}
                        options={page.options ?? []}
                      />
                    </>
                  )}
                  {page.inputType.context === "biometrics" && (
                    <>
                      <Title size="giant">{page.question}</Title>
                      <Text size="md">{page.reason}</Text>
                      <BiometricsPage
                        onAnswer={(biometrics: Biometrics) => {
                          processAnswer({
                            content: biometrics as UserUpdate,
                          });
                          onNext();
                        }}
                      />
                    </>
                  )}
                  {page.inputType.context === "consent" && (
                    <>
                      <Title size="giant">{page.question}</Title>
                      <Text size="md">{page.reason}</Text>
                      <ParentalConsent
                        onAnswer={(parentalConsentData: ParentalConsentData) => {
                          processAnswer({
                            content: parentalConsentData,
                          });
                          onNext();
                        }}
                      />
                    </>
                  )}
                  {page.inputType.context === "streamlinedConsultation" && (
                    <StreamlinedConsultationPage
                      title={page.question ?? ""}
                      reason={page.reason ?? ""}
                      userGender={user.gender ?? Gender.Other}
                      placeholder={page.placeholder ?? ""}
                      onAnswer={(tripDescription: string) => {
                        processAnswer({ content: tripDescription });
                        onNext();
                      }}
                      comfortCareRecommendations={comfortCareRecommendations}
                      setComfortCareRecommendations={setComfortCareRecommendations}
                      selection={streamlinedSelection}
                      regionRecommendations={regionRecommendations}
                      isRegionLoading={isRegionLoading}
                      isAuth={isAuth}
                    />
                  )}
                </>
              )}
              {page.inputType.input === "calendar" && (
                <>
                  <DateRangePicker
                    min={new Date()}
                    onChange={(value: DateRangePickerValue) => {
                      const context = page.inputType.context as keyof (UserUpdate | CaseUpdate);
                      if (context) {
                        const dto: CalendarAnswerDTO<typeof context> = {
                          payload: {
                            [context as keyof (UserUpdate | CaseUpdate)]: {
                              ...value,
                              from: value.from?.toISOString() ?? "",
                              to: value.to?.toISOString() ?? "",
                            },
                          } as { [K in typeof context]: Calendar },
                        };

                        if (value.to && value.from) {
                          // Show notice message if any needs to trigger.
                          const warningMessage = displayWarningMessage(
                            dto.payload,
                            currentPage.warningMessage
                          );
                          setBannerContent(warningMessage);
                          setWarningMessage(warningMessage !== null);
                          setFieldValue(dto.payload);
                        } else {
                          setFieldValue("");
                        }
                      }
                    }}
                    fromTitle="Departure"
                    toTitle="Return"
                  />
                </>
              )}
              {page.inputType.input === "radio" && (
                <div className="rw-page-consultation-list">
                  {(page?.options ?? []).map((option, index) => (
                    <RadioButton
                      data-testid={`questionnaire-radio-button-${index}`}
                      content={option.title}
                      checked={inputChosenOption === option.title}
                      onClick={() => {
                        processAnswer({ content: option.title });
                        setInputChosenOption(option.title);
                      }}
                    />
                  ))}
                  {inputChosenOption === CUSTOM_OPTION_TITLE && (
                    <TextField
                      data-testid="radio-textfield"
                      as="textarea"
                      placeholder={"Please specify."}
                      value={fieldValue === CUSTOM_OPTION_TITLE ? "" : (fieldValue as string)}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setFieldValue(e.target.value)
                      }
                    />
                  )}
                </div>
              )}
              {page.noticeMessage?.isInputError && bannerContent && !warningMessage && (
                <Feedback isInvalid size="sm" className="rw-page-consultation-feedback-message">
                  {page.noticeMessage.message}
                </Feedback>
              )}
              {bannerContent && (!page.noticeMessage?.isInputError || warningMessage) && (
                <AlertBanner
                  className="question-alert-banner"
                  data-testid="question-alert-banner"
                  variant={bannerContent?.variant}
                >
                  <AlertBannerMessage>{bannerContent?.message}</AlertBannerMessage>
                </AlertBanner>
              )}
            </PageConsultation.Body>
            <PageConsultation.Footer>
              {renderNextButton && (
                <Button
                  className="next-button"
                  data-testid="next-button"
                  size="lg"
                  iconName="ArrowRight"
                  customWidth={440}
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                    // Prevents a focus-shift/blur race that can swallow this
                    // click entirely -- see the medication-search-input
                    // domain doc.
                    if (page.inputType.variant === "medication-search") {
                      e.preventDefault();
                    }
                  }}
                  onClick={() => {
                    // If the input brings a validation
                    if (page.inputType.action === "validate") {
                      // Validate answer.
                      const validAnswer = isValidAnswer(
                        page.stateKey,
                        fieldValue?.toString() ?? ""
                      );
                      if (!validAnswer && page.noticeMessage) {
                        setBannerContent({
                          message: page.noticeMessage?.message,
                          variant: page.noticeMessage?.variant,
                        });
                        return;
                      }
                    }

                    const finalFieldValue =
                      page.inputType.variant === "medication-search"
                        ? medicationAutocompleteRef.current?.getCommittedValue() ?? fieldValue
                        : fieldValue;

                    // Set important flag for buttons_then_text and checkbox inputs
                    let important = false;

                    if (page.inputType.input === "buttons_then_text" && finalFieldValue !== "No") {
                      important = page.important?.polarity === "positive";
                    }

                    if (page.inputType.input === "checkbox") {
                      if (finalFieldValue === "None of the above") {
                        important = page.important?.polarity === "negative";
                      } else {
                        important = page.important?.polarity === "positive";
                      }
                    }

                    processAnswer({ content: finalFieldValue ?? "", important });

                    onNext();
                    // Clear local input states
                    setButtonThenTextField(false);
                    setCheckboxTextFieldIsOpen(false);
                    setInputChosenOption(null);
                    setMedicationPendingInput("");
                  }}
                  disabled={
                    ((!fieldValue || (fieldValue as string).length === 0) &&
                      medicationPendingInput.trim().length === 0) ||
                    (checkboxTextFieldIsOpen && !Boolean(checkboxTextFieldValue))
                  }
                >
                  Next
                </Button>
              )}
            </PageConsultation.Footer>
          </PageConsultation>
        )}
      </>
    );
  };

  return <div className="space-y-4">{renderPage(currentPage)}</div>;
}
